import {
  Component,
  JSXElement,
  onCleanup,
  onMount,
  createMemo,
} from 'solid-js';
import { BtnClasses } from '../../types/common';
import type { IconSize } from '../Icon/icon-size';
import { IconColor } from '../Icon/icon-color';
import Button, { ButtonSize } from '../Button/Button';
import { MODUS_ICON_BUTTON_STYLESHEET } from '../../mc-custom-css/mc-icon-button.css';
import { injectInRenderContext } from '../util';

export type IconShape = 'icon-circle' | 'icon-square';
export interface IconButtonProps {
  icon: string;
  clicked?(): void;
  size?: IconSize;
  color?: BtnClasses;
  iconColor?: IconColor;
  shape?: IconShape;
  title?: string;
  disabled?: boolean;
  'data-cy'?: string;
  /** Additional classes to apply to the <button/> element */
  buttonClasses?: string;
  /** Additional classes to apply to the <Icon /> element */
  classes?: string;
  state?: 'active' | 'default';
  ariaLabel?: string;
  /** When true (tc-icon-button host), forwarded to inner Button for Connect icon font. */
  shadowDom?: boolean;
}

function mapShape(shape?: IconShape): 'circle' | 'square' {
  if (shape === 'icon-square') return 'square';
  return 'circle';
}

function mapButtonSize(size?: IconSize): ButtonSize {
  switch (size) {
    case 'icon-tiny':
    case 'icon-small':
      return 'small';
    case 'icon-large':
      return 'large';
    case 'icon-xlarge':
      return 'large';
    case 'icon-medium':
    default:
      return 'medium';
  }
}

function buildButtonClass(buttonClasses: string | undefined): string {
  const classes = ['mc-icon-btn'];
  if (buttonClasses) classes.push(buttonClasses);
  return classes.join(' ');
}

const IconButton: Component<IconButtonProps> = (props): JSXElement => {
  const disableTooltip = createMemo(
    () => props.title == null || props.title.trim().length === 0
  );

  let wcButtonRef: HTMLElement | undefined;

  onMount(() => {
    let cancelled = false;
    customElements.whenDefined('modus-wc-button').then(() => {
      if (cancelled || !wcButtonRef) return;
      injectInRenderContext(
        wcButtonRef,
        'mc-icon-button-style',
        MODUS_ICON_BUTTON_STYLESHEET
      );
    });
    onCleanup(() => {
      cancelled = true;
    });
  });

  return (
    <modus-wc-tooltip
      content={props.title}
      position="bottom"
      disabled={disableTooltip()}
      class="[&_div]:flex"
    >
      <Button
        ref={(el) => {
          wcButtonRef = el;
        }}
        variant="borderless"
        iconOnly
        icon={props.icon}
        iconColor={props.iconColor}
        iconClass={props.classes}
        shape={mapShape(props.shape)}
        size={mapButtonSize(props.size)}
        iconSize={props.size}
        color={props.color}
        disabled={props.disabled}
        state={props.state}
        ariaLabel={props.ariaLabel ?? props.title}
        class={buildButtonClass(props.buttonClasses)}
        onClick={() => props.clicked?.()}
        shadowDom={props.shadowDom}
        data-cy={props['data-cy'] || 'icon-button'}
      />
    </modus-wc-tooltip>
  );
};

export default IconButton;
